import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { enGB } from 'date-fns/locale';
import { Datastream } from 'pages/sandbox/types/datastream';

const headerUser: string = process.env.REACT_APP_X_USER_EMAIL!;
const headerUserToken: string = process.env.REACT_APP_X_USER_TOKEN!;

const fetchDataStream = async (datastreamId: string): Promise<Datastream> => {
  const response = await fetch(
    `https://iot-rest-prod.urban.io/api/v1/datastream/${datastreamId}`,
    {
      method: 'GET',
      headers: {
        accept: 'application/json',
        'X-User-Email': headerUser,
        'X-User-Token': headerUserToken,
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch ${datastreamId}`);
  }

  return response.json();
};

const retryFailed = async (ids: string[], maxRetries = 1, delay = 30000): Promise<Datastream[]> => {
  let attempts = 0;
  let remaining = [...ids];
  let successes: Datastream[] = [];

  while (attempts <= maxRetries && remaining.length > 0) {
    if (attempts > 0) await new Promise(res => setTimeout(res, delay)); // Wait 10s before retry
    const results = await Promise.allSettled(remaining.map(fetchDataStream));

    successes.push(
      ...results
        .filter((r) => r.status === 'fulfilled')
        .map((r: PromiseFulfilledResult<Datastream>) => r.value),
    );

    remaining = results
      .map((r, i) => ({ r, id: ids[i] }))
      .filter(({ r }) => r.status === 'rejected')
      .map(({ id }) => id);

    attempts++;
  }

  return successes;
};

const useDataStreamsForSandBox = (datastreamIds: string[]) => {
  const queryResults = useQuery({
    queryKey: ['dataStreams', datastreamIds],
    queryFn: async () => {
      const results = await Promise.allSettled(
        datastreamIds.map(fetchDataStream),
      );

      const successData = results
        .map((r, i) => ({ r, id: datastreamIds[i] }))
        .filter(({ r }) => r.status === 'fulfilled')
        .map(({ r }) => (r as PromiseFulfilledResult<Datastream>).value);

      const failedIds = results
        .map((r, i) => ({ r, id: datastreamIds[i] }))
        .filter(({ r }) => r.status === 'rejected')
        .map(({ id }) => id);

      // Retry failed ones once after 10 seconds
      const retried = await retryFailed(failedIds);

      return {
        data: [...successData, ...retried],
        errors: failedIds.length > 0 ? failedIds : undefined,
      };
    },
    enabled: datastreamIds.length > 0,
    retry: false,
  });

  return {
    data: queryResults.data?.data,
    errors: queryResults.data?.errors,
    isLoading: queryResults.isLoading,
    refetch: queryResults.refetch,
    lastFetched: queryResults.data?.data
      ? format(new Date(), 'HH:mm dd/MM/yyyy', { locale: enGB })
      : undefined,
  };
};

export default useDataStreamsForSandBox;
